import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/site/Seo";
import { ph } from "@/lib/posthog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Stripe redirects users here with ?session_id=cs_…  We poll the backend
 * until it reports payment paid (with a sensible cap), then show the
 * confirmation + "create your login" CTA.
 */
const Welcome = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id") || (typeof window !== "undefined" && sessionStorage.getItem("cv_last_session")) || null;

  const [state, setState] = useState({ phase: "polling", attempts: 0, data: null, error: null });
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    if (!sessionId) {
      setState({ phase: "missing", attempts: 0, data: null, error: null });
      return;
    }
    const MAX = 20;       // ~60s of polling
    const DELAY = 3000;

    const tick = async (attempt) => {
      if (cancelRef.current) return;
      try {
        const r = await axios.get(`${API}/billing/status/${encodeURIComponent(sessionId)}`);
        if (cancelRef.current) return;
        if (r.data?.status === "paid") {
          ph("checkout_paid", { plan: r.data.plan, email: r.data.email });
          setState({ phase: "paid", attempts: attempt, data: r.data, error: null });
          try { sessionStorage.removeItem("cv_last_session"); } catch (_e) { /* ignore */ }
          return;
        }
        if (r.data?.status === "expired" || r.data?.status === "failed") {
          setState({ phase: r.data.status, attempts: attempt, data: r.data, error: null });
          return;
        }
        if (attempt >= MAX) {
          setState({ phase: "timeout", attempts: attempt, data: r.data, error: null });
          return;
        }
        setState({ phase: "polling", attempts: attempt, data: r.data, error: null });
        setTimeout(() => tick(attempt + 1), DELAY);
      } catch (err) {
        if (cancelRef.current) return;
        if (attempt >= MAX) {
          setState({ phase: "error", attempts: attempt, data: null, error: err?.response?.data?.detail || "Status check failed." });
          return;
        }
        setTimeout(() => tick(attempt + 1), DELAY);
      }
    };

    tick(1);
    return () => { cancelRef.current = true; };
  }, [sessionId]);

  return (
    <>
      <Seo title="Welcome to Clearview" description="You're in — set up your product login to start reading your numbers." path="/welcome" />
      <Header />
      <section className="ph">
        <div className="container">
          <div className="eyebrow eyebrow-light">{state.phase === "paid" ? "Subscription active" : "Confirming your payment"}</div>
          <h1 style={{ marginTop: 14 }}>
            {state.phase === "paid" ? (
              <>You're in.<br /><span className="gtext">Let's get you reading.</span></>
            ) : state.phase === "missing" ? (
              <>We can't see a checkout session.<br /><span className="gtext">Try again from pricing.</span></>
            ) : (
              <>One moment…<br /><span className="gtext">checking with Stripe.</span></>
            )}
          </h1>
          {state.phase === "polling" ? (
            <p className="lead">This usually takes a few seconds. Don't refresh.</p>
          ) : state.phase === "paid" ? (
            <p className="lead">Your subscription is active. The last step is to create your login on the product and connect read-only.</p>
          ) : state.phase === "missing" ? (
            <p className="lead">Looks like you landed here without a session id. <Link to="/pricing" style={{ color: "#2FD3C6", textDecoration: "underline" }}>Pick a plan again →</Link></p>
          ) : state.phase === "timeout" ? (
            <p className="lead">Stripe's still processing. Check your inbox for the receipt — we've recorded your payment and emailed your next step.</p>
          ) : (
            <p className="lead">We couldn't confirm with Stripe yet. {state.error || ""}</p>
          )}
        </div>
      </section>

      <section className="pad" style={{ paddingTop: 60 }}>
        <div className="container narrow-760">
          {state.phase === "paid" && state.data ? (
            <div className="reassure" data-testid="welcome-paid-card">
              <div className="ic">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <h3>Next: create your login</h3>
                <p>
                  We've also emailed this to <b>{state.data.email}</b>. Use the same email there so your subscription matches up.
                </p>
                <p style={{ marginTop: 18 }}>
                  <a className="pill" href={state.data.signup_url} data-testid="welcome-signup-cta">
                    Create your login →
                  </a>
                </p>
                <p style={{ marginTop: 14, fontSize: 13.5, color: "var(--muted-2)" }}>
                  Manage your subscription any time at <Link to="/billing" style={{ color: "var(--p)", fontWeight: 600 }}>/billing</Link>.
                </p>
              </div>
            </div>
          ) : null}

          {state.phase === "polling" ? (
            <div className="cv-spinner" data-testid="welcome-spinner">
              <div className="cv-spin-dot" /><div className="cv-spin-dot" /><div className="cv-spin-dot" />
            </div>
          ) : null}

          {(state.phase === "timeout" || state.phase === "error" || state.phase === "expired" || state.phase === "failed") ? (
            <div className="reassure">
              <div className="ic">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 8v5m0 3.5v.01M12 3l10 18H2L12 3Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <h3>{state.phase === "expired" || state.phase === "failed" ? "Checkout didn't complete" : "We're still confirming"}</h3>
                <p>
                  {state.phase === "expired" || state.phase === "failed"
                    ? "Looks like the checkout didn't go through. You can pick a plan and try again."
                    : "If you completed the payment, check your inbox — your receipt and welcome email will arrive shortly."}
                </p>
                <p style={{ marginTop: 18 }}>
                  <Link className="pill ghost-pill" to="/pricing" data-testid="welcome-back-to-pricing">Back to pricing</Link>
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default Welcome;
